// components/ProtectedRoute.tsx
import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: string[];
  requireAnyRole?: boolean;
  fallbackPath?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles = [],
  requireAnyRole = false,
  fallbackPath = '/login'
}) => {
  const { isAuthenticated, isLoading, hasRole, user } = useAuth();
  const location = useLocation();

  // Mostrar spinner mientras carga
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner-large"></div>
        <p>Verificando autenticación...</p>

        <style>{`
          .loading-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            gap: 16px;
          }

          .spinner-large {
            width: 40px;
            height: 40px;
            border: 4px solid #e5e7eb;
            border-top: 4px solid #667eea;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          p {
            color: #6b7280;
            margin: 0;
          }
        `}</style>
      </div>
    );
  }

  // Redirigir al login si no está autenticado
  if (!isAuthenticated) {
    return (
      <Navigate
        to={fallbackPath}
        state={{ from: location }}
        replace
      />
    );
  }

  // Verificar roles si se especifican
  if (requiredRoles.length > 0) {
    let hasPermission = false;

    if (requireAnyRole) {
      // El usuario necesita al menos uno de los roles
      hasPermission = requiredRoles.some(role => hasRole(role));
    } else {
      // El usuario necesita todos los roles
      hasPermission = requiredRoles.every(role => hasRole(role));
    }

    if (!hasPermission) {
      const rolesString = Array.isArray(user?.roles) ? user.roles.map(r => typeof r === 'string' ? r : r.name).join(', ') : 'Sin roles';
      return (
        <div className="unauthorized-container">
          <div className="unauthorized-content">
            <div className="unauthorized-icon">🔒</div>
            <h2>Acceso Restringido</h2>
            <p>
              No tienes permisos para acceder a esta página.
            </p>
            <div className="user-info">
              <p><strong>Usuario:</strong> {user?.email}</p>
              <p><strong>Roles:</strong> {rolesString}</p>
              <p><strong>Roles requeridos:</strong> {requiredRoles.join(', ')}</p>
            </div>
            <button
              onClick={() => window.history.back()}
              className="back-button"
            >
              Volver
            </button>
          </div>

          <style>{`
            .unauthorized-container {
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              padding: 20px;
              background: #f9fafb;
            }

            .unauthorized-content {
              background: white;
              padding: 40px;
              border-radius: 12px;
              box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
              text-align: center;
              max-width: 500px;
              width: 100%;
            }

            .unauthorized-icon {
              font-size: 48px;
              margin-bottom: 16px;
            }

            .unauthorized-content h2 {
              color: #dc2626;
              margin-bottom: 12px;
              font-size: 24px;
            }

            .unauthorized-content p {
              color: #6b7280;
              margin-bottom: 20px;
              line-height: 1.5;
            }

            .user-info {
              background: #f3f4f6;
              padding: 16px;
              border-radius: 8px;
              margin: 20px 0;
              text-align: left;
            }

            .user-info p {
              margin: 8px 0;
              font-size: 14px;
            }

            .back-button {
              background: #667eea;
              color: white;
              border: none;
              padding: 12px 24px;
              border-radius: 8px;
              font-size: 16px;
              font-weight: 500;
              cursor: pointer;
              transition: background-color 0.2s ease;
            }

            .back-button:hover {
              background: #5a67d8;
            }
          `}</style>
        </div>
      );
    }
  }

  // Usuario autenticado y con permisos
  return <>{children}</>;
};

// Componente específico para admins
interface RouteProps {
  children: ReactNode;
}

export const AdminRoute: React.FC<RouteProps> = ({ children }) => (
  <ProtectedRoute requiredRoles={['admin']}>
    {children}
  </ProtectedRoute>
);

// Componente para managers y admins
export const ManagerRoute: React.FC<RouteProps> = ({ children }) => (
  <ProtectedRoute requiredRoles={['admin', 'manager']} requireAnyRole>
    {children}
  </ProtectedRoute>
);

// Componente para cualquier usuario autenticado
export const AuthenticatedRoute: React.FC<RouteProps> = ({ children }) => (
  <ProtectedRoute>
    {children}
  </ProtectedRoute>
);

export default ProtectedRoute;